import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ButtonDS, RadioButton, TextInput, Typography, useDevice } from "get-life-storybook-ts";
import Add from "../../../../../components/Components/Add/Add";
import Remove from "../../../../../components/Components/Remove/Remove";
import HttpLeadRepository from "../../../../../api/request/Lead/Lead.service";
import { GetlifeContext } from "../../../../../context/GetLifeContext.context";
import { I18nContext } from "../../../../../context/i18n.context";
import { trackSubmissionError, trackValidationError } from "../../../../../helpers/Tracking";
import "../../../css/questions.scss";
import "../../../../../components/Components/FormBuilder/formBuilder.scss";
import "../../../../../components/Components/forms/beneficiaries/formPhysicalPerson.scss";
import {
  BankRowI,
  CLAUSE_LIMITS,
  PersonRowI,
  SubsidiaryModeT,
  bankRowValid,
  bankRows,
  emptyBank,
  emptyPerson,
  exclusivePayload,
  exclusiveValid,
  modalityFromRouteParam,
  personRowValid,
  personRows,
  pledgePayload,
  pledgeValid,
} from "./beneficiaryClause";

const BENEFICIARIES_STEP = "/coverage/2";
const NEXT_STEP = "/coverage/7";

type TranslateT = (key: string) => string;

// Same blocks as the rest of the funnel: the Question title, the grid-beneficiary
// rows of the physical-person form and the line + Add/Remove separators.
// Module-level on purpose: declared inside the screen they would get a new
// identity on every render and remount the inputs on every keystroke.

const GroupTitle = ({ text }: { text: string }) => (
  <Typography variant="Body" fontFamily="DM" weight="700" text={text} className="text-left w-full pb-[12px]" />
);

const RowLabel = ({ text }: { text: string }) => (
  <Typography
    variant="SpanMedium"
    fontFamily="DM"
    weight="700"
    text={text}
    className="text-left w-full text-[#424242] pb-[8px]"
  />
);

const Field = ({
  label,
  value,
  placeholder,
  error,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  error?: string;
  onChange: (value: string) => void;
}) => (
  <div>
    <TextInput label={label} initialValue={value} placeholder={placeholder} onValueChange={onChange} />
    {error && <p>{error}</p>}
  </div>
);

const PersonFields = ({
  person,
  title,
  showErrors,
  requiredText,
  t,
  translate,
  onChange,
}: {
  person: PersonRowI;
  title?: string;
  showErrors: boolean;
  requiredText: string;
  t: TranslateT;
  translate: TranslateT;
  onChange: (person: PersonRowI) => void;
}) => {
  const errorOf = (value: string) => (showErrors && value.trim() === "" ? requiredText : undefined);
  return (
    <div className="grid-beneficiary">
      {title && <RowLabel text={title} />}
      <div className="row">
        <Field
          label={t("personName")}
          value={person.name}
          placeholder={translate("payment.personalData.namePlaceHolder")}
          error={errorOf(person.name)}
          onChange={(name) => onChange({ ...person, name })}
        />
      </div>
      <div className="row">
        <Field
          label={t("personLastName")}
          value={person.lastName}
          placeholder={translate("payment.personalData.surnamePlaceHolder")}
          error={errorOf(person.lastName)}
          onChange={(lastName) => onChange({ ...person, lastName })}
        />
        <Field
          label={t("personLastName2")}
          value={person.lastName2}
          placeholder="Ruiz"
          onChange={(lastName2) => onChange({ ...person, lastName2 })}
        />
      </div>
      <div className="row">
        <Field
          label={t("personId")}
          value={person.idNumber}
          placeholder="00000000W"
          error={errorOf(person.idNumber)}
          onChange={(idNumber) => onChange({ ...person, idNumber })}
        />
      </div>
    </div>
  );
};

const BankFields = ({
  bank,
  title,
  showErrors,
  requiredText,
  t,
  onChange,
}: {
  bank: BankRowI;
  title: string;
  showErrors: boolean;
  requiredText: string;
  t: TranslateT;
  onChange: (bank: BankRowI) => void;
}) => {
  const errorOf = (value: string) => (showErrors && value.trim() === "" ? requiredText : undefined);
  return (
    <div className="grid-beneficiary">
      <RowLabel text={title} />
      <div className="row">
        <Field
          label={t("bankName")}
          value={bank.bankName}
          error={errorOf(bank.bankName)}
          onChange={(bankName) => onChange({ ...bank, bankName })}
        />
        <Field
          label={t("loanNumber")}
          value={bank.loanNumber}
          error={errorOf(bank.loanNumber)}
          onChange={(loanNumber) => onChange({ ...bank, loanNumber })}
        />
      </div>
    </div>
  );
};

const Separator = ({ children }: { children: JSX.Element }) => (
  <div className="formPhysicalPerson-addContainer">
    <div className="formPhysicalPerson-line"></div>
    {children}
  </div>
);

const updateAt = <T,>(list: T[], index: number, value: T): T[] =>
  list.map((item, i) => (i === index ? value : item));

const removeAt = <T,>(list: T[], index: number): T[] => list.filter((_, i) => i !== index);

/**
 * Pledge or exclusive designation in the contracting funnel (ES / AXA_WL). Reached
 * from the beneficiaries question; on save the backend writes the clause, recomputes
 * the lead status and the funnel goes on as after any other designation.
 */
const BeneficiaryClause = () => {
  const { modality: modalityParam } = useParams();
  const modality = modalityFromRouteParam(modalityParam);
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification, setProgressBar } = useContext(GetlifeContext);

  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [banks, setBanks] = useState<BankRowI[]>(() => [emptyBank()]);
  const [remainder, setRemainder] = useState<PersonRowI[]>(() => [emptyPerson()]);
  const [principal, setPrincipal] = useState<PersonRowI>(() => emptyPerson());
  const [subsidiaryMode, setSubsidiaryMode] = useState<SubsidiaryModeT>("EQUAL_PARTS");
  const [subsidiaries, setSubsidiaries] = useState<PersonRowI[]>(() => [emptyPerson()]);

  const t: TranslateT = (key) => translate(`newDashboard.modifications.beneficiaries.${key}`);
  const requiredText = translate("beneficiaries.error.required");
  const numbered = (key: string, index: number) => t(key).replace("{n}", String(index + 1));

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (!modality) {
      navigate(BENEFICIARIES_STEP);
      return;
    }
    setProgressBar(90);
    if (!leadId || !token) return;
    new HttpLeadRepository(leadId, token)
      .getBeneficiaryDesignation()
      .then(({ available, designation }) => {
        if (!available) {
          navigate(BENEFICIARIES_STEP);
          return;
        }
        if (designation?.modality === "PLEDGE_CLAUSE" && modality === "PLEDGE_CLAUSE") {
          setBanks(bankRows(designation.banks));
          setRemainder(personRows(designation.remainderBeneficiaries));
        }
        if (designation?.modality === "EXCLUSIVE" && modality === "EXCLUSIVE") {
          setPrincipal(personRows([designation.principal])[0]);
          setSubsidiaryMode(designation.subsidiaryMode);
          setSubsidiaries(personRows(designation.subsidiaries));
        }
      })
      .catch((error: any) => {
        if (error?.status === 401) navigate("/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modality, leadId, token]);

  if (!modality) return null;

  const isPledge = modality === "PLEDGE_CLAUSE";
  const valid = isPledge ? pledgeValid(banks, remainder) : exclusiveValid(principal, subsidiaries);

  const submit = async () => {
    if (submitting) return;
    if (!valid) {
      // Faltan datos obligatorios o el reparto no cuadra.
      trackValidationError(isPledge ? "pledge_clause" : "exclusive_clause", "format");
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    try {
      await new HttpLeadRepository(leadId, token).postBeneficiaryDesignation(
        isPledge
          ? pledgePayload(banks, remainder)
          : exclusivePayload(principal, subsidiaryMode, subsidiaries)
      );
      navigate(NEXT_STEP);
    } catch (error: any) {
      trackSubmissionError(error);
      if (error?.status === 401) {
        navigate("/login");
        return;
      }
      setNotification({
        message: translate("newDashboard.modifications.errors.generic"),
        type: "error",
        time: 5,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const peopleEditor = (
    people: PersonRowI[],
    setPeople: (people: PersonRowI[]) => void,
    max: number,
    titleOf: (index: number) => string
  ) => (
    <>
      {people.map((person, index) => (
        <Fragment key={person.uid}>
          <PersonFields
            person={person}
            title={titleOf(index)}
            showErrors={showErrors && !personRowValid(person)}
            requiredText={requiredText}
            t={t}
            translate={translate}
            onChange={(value) => setPeople(updateAt(people, index, value))}
          />
          {people.length > 1 && (
            <Separator>
              <Remove content={t("remove")} onClick={() => setPeople(removeAt(people, index))} />
            </Separator>
          )}
        </Fragment>
      ))}
      {people.length < max && (
        <Separator>
          <Add content={t("addBeneficiary")} onClick={() => setPeople([...people, emptyPerson()])} />
        </Separator>
      )}
    </>
  );

  return (
    <div className="questions-outerContainer">
      <div className="questions-container">
        <div className="questions-innerContainer">
          <div className="formBuilder-container">
            <div className="flex flex-col items-center w-full pb-[30px]">
              <div className="flex flex-row items-center pb-[16px]">
                <Typography
                  text=""
                  variant="H3"
                  fontFamily="DM"
                  weight="700"
                  className="text-left flex !flex-row w-full gap-2 text-[22px]"
                >
                  <span>{t(`modality.${modality}.title`)}</span>
                </Typography>
              </div>
              <Typography
                text={t(isPledge ? "pledgeDesc" : "exclusiveDesc")}
                variant="SpanMedium"
                fontFamily="DM"
                weight="400"
                className="w-full text-center opacity-50 font-help-text-gray text-input-help-text"
              />
            </div>

            <div className="formPhysicalPerson-container">
              <div className="formPhysicalPerson-GridContainer">
                {isPledge ? (
                  <>
                    <GroupTitle text={t("banksTitle")} />
                    {banks.map((bank, index) => (
                      <Fragment key={bank.uid}>
                        <BankFields
                          bank={bank}
                          title={numbered("bankN", index)}
                          showErrors={showErrors && !bankRowValid(bank)}
                          requiredText={requiredText}
                          t={t}
                          onChange={(value) => setBanks(updateAt(banks, index, value))}
                        />
                        {banks.length > 1 && (
                          <Separator>
                            <Remove content={t("remove")} onClick={() => setBanks(removeAt(banks, index))} />
                          </Separator>
                        )}
                      </Fragment>
                    ))}
                    {banks.length < CLAUSE_LIMITS.banks && (
                      <Separator>
                        <Add content={t("addBank")} onClick={() => setBanks([...banks, emptyBank()])} />
                      </Separator>
                    )}

                    <GroupTitle text={t("remainderTitle")} />
                    {peopleEditor(remainder, setRemainder, CLAUSE_LIMITS.remainder, (i) =>
                      numbered("beneficiaryN", i)
                    )}
                    {remainder.length > 1 && (
                      <Typography
                        text={t("pledgeEqualSplitNote")}
                        variant="SpanMedium"
                        fontFamily="DM"
                        weight="400"
                        className="w-full text-left opacity-50 font-help-text-gray text-input-help-text"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <GroupTitle text={t("principalTitle")} />
                    <PersonFields
                      key={principal.uid}
                      person={principal}
                      showErrors={showErrors && !personRowValid(principal)}
                      requiredText={requiredText}
                      t={t}
                      translate={translate}
                      onChange={setPrincipal}
                    />

                    <div className="flex flex-col gap-4 w-full pt-[40px] pb-[40px]">
                      <GroupTitle text={t("subsidiaryModeTitle")} />
                      {(["EQUAL_PARTS", "ORDERED"] as const).map((mode) => (
                        <RadioButton
                          key={mode}
                          variant="Radio"
                          size="S"
                          label={t(`subsidiaryMode.${mode}`)}
                          value={mode}
                          checked={subsidiaryMode === mode}
                          onChange={() => setSubsidiaryMode(mode)}
                        />
                      ))}
                    </div>

                    <GroupTitle text={t("subsidiariesTitle")} />
                    {peopleEditor(subsidiaries, setSubsidiaries, CLAUSE_LIMITS.subsidiaries, (i) =>
                      subsidiaryMode === "ORDERED" ? `${i + 1}º` : numbered("beneficiaryN", i)
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={isMobile ? "mobileShow" : "buttonsContainer"}>
          <div
            className={
              isMobile
                ? "formBuilder-buttonContainer-mobile formBuilder-buttonContainer-mobile-primary"
                : "formBuilder-buttonContainer"
            }
          >
            <ButtonDS
              label={translate("continuar")}
              onClick={submit}
              disabled={submitting}
              className="w-full"
              rightIcon="ArrowRightIcon"
            />
          </div>
          <div
            className={
              isMobile
                ? "formBuilder-buttonContainer-mobile goBackButtonWidthMobile"
                : "formBuilder-buttonContainer"
            }
          >
            <ButtonDS
              label={translate("questions.button.goBack")}
              onClick={() => navigate(BENEFICIARIES_STEP)}
              buttonType="secondary"
              ghost={true}
              className="w-full"
              leftIcon="ArrowBackIcon"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryClause;
